import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import PORTRAIT from "@/assets/portrait.jpg";

const credentials = [
  { num: "I.", text: "Okleveles svéd- és frissítő masszőr" },
  { num: "II.", text: "Több éves stúdió- és magángyakorlat" },
  { num: "III.", text: "Folyamatos szakmai továbbképzés" },
  { num: "IV.", text: "Diszkrét, prémium környezet" },
];

const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const portraitY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section
      id="about"
      ref={ref}
      data-testid="about-section"
      className="relative pt-20 pb-28 md:py-40 lg:py-48 bg-anthracite-950 overflow-hidden"
    >
      {/* Decorative big number */}
      <div className="absolute top-20 right-0 font-serif text-[18rem] md:text-[26rem] text-white/[0.025] leading-none pointer-events-none select-none">
        01
      </div>

      <div className="max-w-[1500px] mx-auto px-6 md:px-10 lg:px-14 relative">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-start">
          {/* Image column - offset */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="col-span-12 md:col-span-5 lg:col-span-5 relative md:sticky md:top-32"
          >
            <div className="relative">
              {/* Small label above image */}
              <div className="absolute -top-8 left-0 flex items-center gap-3">
                <span className="text-[10px] uppercase tracking-[0.4em] text-gold-500/80">
                  N° I — Portré
                </span>
                <div className="w-10 h-px bg-gold-500/40" />
              </div>

              <div className="relative aspect-[4/5] overflow-hidden">
                <motion.img
                  style={{ y: portraitY }}
                  src={PORTRAIT}
                  alt="Rostás Ibolya — NOBLESSE"
                  data-testid="about-portrait"
                  className="w-full h-[110%] object-cover grayscale-[10%] hover:grayscale-0 transition-all duration-1000"
                />
                <div className="absolute inset-0 ring-1 ring-gold-500/20 pointer-events-none" />
              </div>

              {/* Floating signature card */}
              <div className="absolute -bottom-8 sm:-bottom-12 right-2 md:-right-12 bg-anthracite-950 border border-gold-500/30 px-5 py-4 sm:px-6 sm:py-5 max-w-[220px] sm:max-w-[240px]">
                <div className="text-[9px] uppercase tracking-[0.35em] sm:tracking-[0.4em] text-gold-500 mb-1.5 sm:mb-2">
                  Anno · Budapest
                </div>
                <div className="font-serif italic text-xl sm:text-2xl text-cream">Rostás Ibolya</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-cream/40 mt-1">
                  Alapító · Masszőr
                </div>
              </div>
            </div>
          </motion.div>

          {/* Text column */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            className="col-span-12 md:col-span-7 lg:col-span-6 lg:col-start-7 md:pt-12"
          >
            <div className="flex items-center gap-4 mb-8">
              <span className="text-[10px] uppercase tracking-[0.4em] text-gold-500/80">
                01 — Bemutatkozás
              </span>
              <div className="w-16 h-px bg-gold-500/40" />
            </div>

            <h2
              data-testid="about-title"
              className="font-serif text-cream leading-[1.02] tracking-[-0.02em] font-light"
            >
              <span className="block text-4xl md:text-5xl lg:text-6xl">A legjobb kezekben,</span>
              <span className="block text-4xl md:text-5xl lg:text-6xl italic gold-gradient-text mt-2">
                valódi figyelemmel.
              </span>
            </h2>

            {/* Pull quote */}
            <div className="mt-12 relative pl-8 border-l border-gold-500/40">
              <span className="absolute -left-3 -top-4 font-serif text-6xl text-gold-500/60 leading-none">"</span>
              <p
                data-testid="about-bio-1"
                className="font-serif italic text-xl md:text-2xl text-cream/90 leading-snug font-light"
              >
                Nálam nem csupán egy masszázst kap, hanem egy tudatosan felépített
                terápiás élményt.
              </p>
            </div>

            <p
              data-testid="about-bio-2"
              className="mt-10 text-cream/65 font-light text-base md:text-lg leading-relaxed max-w-lg"
            >
              Magasan képzett szaktudásom és tiszta környezet garantálja, hogy Ön
              a legjobb kezekben legyen – legyen szó regenerációról vagy a
              mindennapi stressz oldásáról. Engedje meg, hogy szakértelmemmel
              segítsem Önt a testi-lelki egyensúly visszaállításában.
            </p>

            {/* Credentials */}
            <div className="mt-14">
              <div className="flex items-center gap-4 mb-8">
                <span className="text-[10px] uppercase tracking-[0.4em] text-gold-500/80">Szakmai háttér</span>
                <div className="flex-1 h-px bg-gold-500/15" />
              </div>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-y-5 gap-x-8">
                {credentials.map((c, i) => (
                  <li
                    key={i}
                    data-testid={`about-credential-${i}`}
                    className="flex items-start gap-4 group"
                  >
                    <span className="font-serif italic text-gold-500 text-xl leading-none mt-0.5 min-w-[28px]">
                      {c.num}
                    </span>
                    <span className="text-cream/80 text-sm md:text-base font-light leading-snug group-hover:text-cream transition-colors">
                      {c.text}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
